import React, {useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import CONTENT_STRINGS from '../helpers/contentStrings';
import Notifications from "../components/notifications";
import {
	changeNotificationsField,
	clearNotificationsFilters,
	getNotificationTypes,
	requestGetAllNotifications,
	requestPatchNotification,
	selectActiveTabNotification,
} from "../actions/notifications";
import {requestGetSelectedApplication} from "../actions/newApplication";
import {requestGetApplicationStatuses} from "../actions/applicationStatuses";
import {receiveApplicationNotification} from "../actions/applicationPolicy";
import {getCaseTypes} from "../actions/caseTypes";
import {isAragUser, isSolicitorUser} from "../helpers/functions";
import {getSolicitorGroupCaseTypes} from "../actions/groups";
import {requestGetUpdateQuestions} from "../actions/updateTab";
import {
	NOTIFICATION_TYPE_MN_UPDATED,
	POLICY_STATUS_MN_DRAFT,
	POLICY_STATUS_MN_REFERRED,
	NOTIFICATION_TYPE_MN_UPDATE_REQUIRED,
	NOTIFICATION_TYPE_MN_MORE_INFORMATION_REQUIRED,
	NOTIFICATION_TYPE_MN_INFORMATION_PROVIDED,
	NOTIFICATION_TYPE_MN_DISBURSEMENT,
	NOTIFICATION_TYPE_MN_REQUEST_FOR_APP_FORM_CHANGE,
	NOTIFICATION_TYPE_MN_APP_FORM_CHANGE_REJECTED,
	NOTIFICATION_TYPE_MN_APP_FORM_CHANGED,
	NOTIFICATION_TYPE_MN_UPDATE_COMPLETED_BY_ARAG,
	NOTIFICATION_TYPE_MN_NO_CHANGES,
} from "../helpers/constants";
import { useHistory } from 'react-router-dom';

const getRequestParams = (notifications) => {
	const calcOffset = notifications.selectedView * (notifications.page - 1);
	return {
		limit: notifications.selectedView,
		offset: calcOffset > 0 ? calcOffset : 0,
		shallow: true,
		selectedUpdateFormQuestions: notifications.selectedUpdateFormQuestions,
		selectedNotificationTypes: notifications.selectedNotificationTypes,
		selectedCaseTypes: notifications.selectedCaseTypes,
		selectedStatuses: notifications.selectedStatuses,
	}
};

const getNotificationActiveTab = (eventMachineName) => {
	if([NOTIFICATION_TYPE_MN_UPDATE_REQUIRED, NOTIFICATION_TYPE_MN_UPDATED, NOTIFICATION_TYPE_MN_NO_CHANGES].includes(eventMachineName)) return 'update';
	if([NOTIFICATION_TYPE_MN_MORE_INFORMATION_REQUIRED, NOTIFICATION_TYPE_MN_INFORMATION_PROVIDED].includes(eventMachineName)) return 'overview';
	if(eventMachineName === NOTIFICATION_TYPE_MN_DISBURSEMENT) return 'advancedDisbursements';
	if(eventMachineName === NOTIFICATION_TYPE_MN_REQUEST_FOR_APP_FORM_CHANGE) return 'pendingChanges';
	if([NOTIFICATION_TYPE_MN_APP_FORM_CHANGE_REJECTED, NOTIFICATION_TYPE_MN_APP_FORM_CHANGED, NOTIFICATION_TYPE_MN_UPDATE_COMPLETED_BY_ARAG].includes(eventMachineName)) return 'overview';
	return 'application';
};

const NotificationsHOC = () => {
	const dispatch = useDispatch();
	const notifications = useSelector(state => state.notifications);
	const user = useSelector(state => state.user);
	const caseTypes = useSelector(state => state.caseTypes);
	const applicationStatuses = useSelector(state => state.applicationStatuses);
	const solicitorCaseTypes = useSelector(state => state.newApplication.caseTypes);
	const updateFormQuestions = useSelector(state => state.updateTab.questions);
	const fetchingUpdateFormQuestions = useSelector(state => state.updateTab.fetching);
	const fetchingCaseTypes = useSelector(state => state.caseTypes.fetching);
	const fetchingApplicationStatuses = useSelector(state => state.applicationStatuses.fetching);

	const containerRef = useRef(null);
	const prevPageRef = useRef(notifications.page);
	const history = useHistory();
	const isArag = isAragUser(user.loggedInUser.roles);
	const isSolicitor = isSolicitorUser(user.loggedInUser.roles);

	useEffect(() => {
		dispatch(requestGetApplicationStatuses());
		if(isArag){
			dispatch(getCaseTypes());
			dispatch(requestGetUpdateQuestions());
			dispatch(getNotificationTypes());
		}
		if(isSolicitor && user.loggedInUser.solicitorGroupId){
			dispatch(getSolicitorGroupCaseTypes(user.loggedInUser.solicitorGroupId));
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if(notifications.wasFilterButtonPressed) return;
		const parameters = getRequestParams(notifications);
		dispatch(requestGetAllNotifications(parameters));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notifications.selectedView, notifications.page, notifications.wasFilterButtonPressed]);

	useEffect(() => {
		if(prevPageRef.current !== notifications.page) {
			containerRef.current && containerRef.current.scrollIntoView();
		}
		prevPageRef.current = notifications.page;
	}, [notifications.page]);

	const getNotificationStatusText = (notification) => {
		if(!notification.applicationStatusName.includes(POLICY_STATUS_MN_REFERRED)){
			return notification.applicationStatusName;
		} else {
			if(isArag){
				return notification.applicationStatusName + ' to ' + notification.referredTo;
			} else {
				return notification.applicationStatusName;
			}
		}
	};

	const handleNotificationClick = (item) => {
		const activeTab = getNotificationActiveTab(item.eventMachineName);
		dispatch(requestPatchNotification(item.id, true, item.applicationId));
		dispatch(requestGetSelectedApplication(item.applicationId));
		setTimeout(() => dispatch(receiveApplicationNotification(item)), 0);
		setTimeout(() => dispatch(selectActiveTabNotification(activeTab, item.id, item.eventName)), 0);
		history.push({
			pathname: "/application-and-policies/application-policy/" + item.applicationId,
		});
	};

	const totalDisplayBoxes = Math.ceil(
		parseInt(notifications.totalRecords) / parseInt(notifications.selectedView)
	);

	const selectUpdateFormQuestions = (questions) => {
		dispatch(changeNotificationsField('selectedUpdateFormQuestions', questions ?? []));
	};

	const selectApplicationStatus = (statuses) => {
		const safeStatuses = statuses ?? [];

		if(safeStatuses.find(item => item.value === POLICY_STATUS_MN_DRAFT) && safeStatuses.length === 1){
			dispatch(changeNotificationsField('selectedUpdateFormQuestions', []));
		}
		dispatch(changeNotificationsField('selectedStatuses', safeStatuses));
	};

	const selectCaseTypes = (caseTypesArg) => {
		dispatch(changeNotificationsField('selectedCaseTypes', caseTypesArg ?? []));
	};

	const handleFilterClick = () => {
		dispatch(changeNotificationsField('wasFilterButtonPressed', true));
		const parameters = getRequestParams(notifications);
		dispatch(requestGetAllNotifications(parameters));
	};

	const shouldDisplayUpdateFormQuestionsFilter = () => {
		return (
			(
				(
					notifications.selectedStatuses.find(item => item.value === POLICY_STATUS_MN_DRAFT)
					&& notifications.selectedStatuses.length !== 1
				)
				|| (
					!notifications.selectedStatuses.find(item => item.value === POLICY_STATUS_MN_DRAFT)
				)
			)
			&& notifications.selectedNotificationTypes.find(item => item.value === NOTIFICATION_TYPE_MN_UPDATED)
		)
	};

	const selectNotificationTypes = (notificationTypes) => {
		const safeNotificationTypes = notificationTypes ?? [];
		if(
			notifications.selectedNotificationTypes.find(item => item.value === NOTIFICATION_TYPE_MN_UPDATED)
			&& !safeNotificationTypes.find(item => item.value === NOTIFICATION_TYPE_MN_UPDATED)
		){
			dispatch(changeNotificationsField('selectedUpdateFormQuestions', []));
		}
		dispatch(changeNotificationsField('selectedNotificationTypes', safeNotificationTypes));
	};

	const handleInputChange = (key, value) => dispatch(changeNotificationsField(key, value));

	const handleGoAtPrevious = () => {
		if(notifications.page > 1) {
			dispatch(changeNotificationsField('page', notifications.page - 1));
		}
	};

	const handleGoAtNext = () => {
		if(notifications.page < totalDisplayBoxes) {
			dispatch(changeNotificationsField('page', notifications.page + 1));
		}
	};

	const handleGoAtStart = () => {
		dispatch(changeNotificationsField('page', 1));
	};

	const handleGoAtEnd = () => {
		dispatch(changeNotificationsField('page', totalDisplayBoxes));
	};

	const handleGoAtPage = (page) => {
		dispatch(changeNotificationsField('page', page));
	};

	const statusesOptions = applicationStatuses.statuses
		.map(item => ({value: item.machineName, label: item.name}));



	const mapToOption = (item) => ({value: item.machineName, label: item.name});
	const caseTypesOptions = isArag
		? caseTypes.cases.map(mapToOption)
		: isSolicitor
			? solicitorCaseTypes.map(mapToOption)
			: [];
	const notificationTypesOptions = notifications.notificationTypes
		.map(item => ({value: item.machineName, label: item.name}));
	const updateFormQuestionOptions = updateFormQuestions
		.filter(item => item.isTopLevel)
		.map(item => ({value: item.machineName, label: item.name}));

	return(
		<Notifications
			containerRef={containerRef}
			fetchingApplicationStatuses={fetchingApplicationStatuses}
			fetchingCaseTypes={fetchingCaseTypes}
			fetchingUpdateFormQuestions={fetchingUpdateFormQuestions}
			selectNotificationTypes={selectNotificationTypes}
			shouldDisplayUpdateFormQuestionsFilter={shouldDisplayUpdateFormQuestionsFilter}
			clearNotificationsFilters={() => dispatch(clearNotificationsFilters())}
			handleFilterClick={handleFilterClick}
			selectCaseTypes={selectCaseTypes}
			selectApplicationStatus={selectApplicationStatus}
			selectUpdateFormQuestions={selectUpdateFormQuestions}
			getNotificationStatusText={getNotificationStatusText}
			onNotificationClick={handleNotificationClick}
			Store={notifications}
			goAtPrevious={handleGoAtPrevious}
			goAtNext={handleGoAtNext}
			goAtStart={handleGoAtStart}
			goAtEnd={handleGoAtEnd}
			goAtPage={handleGoAtPage}
			HandleInputChange={handleInputChange}
			caseTypesOptions={caseTypesOptions}
			statusesOptions={statusesOptions}
			notificationTypesOptions={notificationTypesOptions}
			updateFormQuestionOptions={updateFormQuestionOptions}
			totalDisplayBoxes={totalDisplayBoxes}
			isAragUser={isArag}
		/>
	);
};

export default NotificationsHOC;